<template>
	<section class="offline-popup">
		<div class="offline-popup__rewards">
			<RaggedBoxBackground
				slug="offline-rewards-box-bg"
				:skin="{ stopColor1: '#f2dcb5', stopColor2: '#fff3dd' }"
			/>
			<div
				class="offline-rewards"
				v-if="rewards && Object.keys(rewards).length"
			>
				<template
					v-for="([icon, value], index) in Object.entries(rewards)"
					:key="index"
				>
					<div class="offline-reward" v-if="value > 0">
						<span class="offline-reward__value">
							{{
								`${value >= 0 ? "+" : ""}${formatNumber(value)}`
							}}
						</span>
						<AppMainIcon
							class="offline-reward__icon"
							:iconName="icon.toLowerCase()"
							:iconSize="20"
						/>
					</div>
				</template>
			</div>
			<div v-if="offlineAttackClicks" class="offline-popup__rewards-info">
				<strong>{{ "+" + formatNumber(offlineAttackClicks) }}</strong>
				{{ translate("ui.direct_attacks") }}
			</div>
			<div class="offline-popup__rewards-info">
				{{ offlineTimeText }}
			</div>
		</div>
		<div class="offline-popup__buttons">
			<AppTextButton
				class="offline-popup__btn"
				:disabled="isPending"
				:type="
					showMoreButton && !hasAdReward
						? StatusTypes.INFO
						: StatusTypes.SUCCESS
				"
				:text="translate('button.claim').toUpperCase()"
				@click="claimRewards"
			/>
			<AppTextButton
				v-if="showMoreButton && !hasAdReward"
				class="offline-popup__btn"
				:disabled="isPending"
				:type="StatusTypes.PRIMARY"
				:text="translate('ui.percent_more').replace('{percent}', '50%')"
				@click="moreRewards"
			/>
		</div>
		<template v-if="recommendedUpgrade">
			<DividerLine />
			<div class="offline-popup__upgrade">
				<div class="offline-popup__upgrade-heading">
					{{ translate("ui.recommended_upgrades") }}
				</div>
				<UpgradeBox
					@click="toUpgrades"
					:disabled="isPending"
					:has-redirect="true"
					:skin="SkinTypes.LIGHT"
					:id="recommendedUpgrade[0]"
					:heading="recommendedUpgrade[1]?.heading.toString()"
					:description="recommendedUpgrade[1]?.description"
					:descriptionReplace="
						recommendedUpgrade[1]?.descriptionReplace
					"
				/>
			</div>
		</template>
	</section>
</template>

<script lang="ts" setup>
import AppMainIcon from "@/components/AppMainIcon.vue";
import AppTextButton from "@/components/AppTextButton.vue";
import OfflineMorePopup from "@/components/Offline/OfflineMorePopup.vue";
import DividerLine from "@/components/SVG/DividerLine.vue";
import RaggedBoxBackground from "@/components/SVG/RaggedBoxBackground.vue";
import UpgradeBox from "@/components/Upgrades/UpgradeBox.vue";
import {
	OFFLINE_INCOME_DEFAULT_MULTIPLIER,
	OFFLINE_INCOME_WITH_AD_MULTIPLIER,
} from "@/config/constants";
import { showIntroPopup, tryShowIntroPopup } from "@/event";
import type { ResourceType } from "@/game/entities/config/resources";
import type { UpgradeId, UpgradeItem } from "@/game/entities/config/upgrades";
import { upgrades } from "@/game/entities/config/upgrades";
import { OFFLINE_ONE_FRIENDLY_ATTACK_CLICK_PER_TICK_COUNT } from "@/game/SiegeSystemRepository";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useGameTicker from "@/hooks/useGameTicker";
import useSegmentManager from "@/hooks/useSegmentManager";
import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository";
import useSocketService, { EmitStores } from "@/hooks/useSocketService";
import useTileRepository from "@/hooks/useTileRepository";
import { GameTickerLockReason } from "@/libs/GameTicker";
import useNewsStore from "@/store/useNewsStore";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useQuestStore from "@/store/useQuestStore";
import useResourcesStore from "@/store/useResourcesStore";
import useRuntimeStore, { RecordedActivity } from "@/store/useRuntimeStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import useUpgradesStore from "@/store/useUpgradesStore";
import useUserStore from "@/store/useUserStore";
import { SkinTypes, StatusTypes } from "@/types/ui";
import { consumePostOfflineCallback } from "@/utils/deepLinkHandler";
import { formatNumber } from "@/utils/format";
import { floorNumber } from "@/utils/number";
import { translate } from "@/utils/translate";
import { storeToRefs } from "pinia";
import { computed, markRaw, onMounted, onUnmounted, ref } from "vue";

const emit = defineEmits(["closePopup"]);

const ticker = markRaw(useGameTicker());
const emitter = markRaw(useEmitter());
const tilerepo = markRaw(useTileRepository());
const segmentManager = markRaw(useSegmentManager());
const socketService = markRaw(useSocketService());

const userStore = useUserStore();
const {
	lastSeenNewsTimestamp,
	daysAfterRegistration,
	lastDailyRewardPickedDaysAfterReg,
} = storeToRefs(userStore);

const props = defineProps<{
	isAdWatched?: boolean;
}>();

const resourcesStore = useResourcesStore();
const settlementsStore = useSettlementsStore();
const questsStore = useQuestStore();
const upgradesStore = useUpgradesStore();
const { setLastRecordedActivity } = useRuntimeStore();

const moreButtonTime: number = 5 * 60 * 1_000;
const recommendedUpgradeId: number = 6;

const rewards = ref<Partial<Record<ResourceType, number>>>({});
const hasAdReward = ref<boolean>(false);
const isPending = ref<boolean>(false);

const newsStore = useNewsStore();
const { activePopupNews } = storeToRefs(newsStore);
const popupStore = usePopupStore();

// const offlineSiegeBoosters = computed((): number =>
// 	Math.trunc(tilerepo.getOfflineTickCount() / 6),
// );

const offlineAttackClicks = computed<number | null>((): number | null => {
	if (!useSiegeSystemRepository().isActive) return null;
	return Math.trunc(
		tilerepo.getOfflineTickCount() /
			OFFLINE_ONE_FRIENDLY_ATTACK_CLICK_PER_TICK_COUNT,
	);
});

const rawOfflineMinutes = computed((): number =>
	Math.trunc((userStore.lastTickBefore || 0) / 60 / 1_000),
);

const rawMaxOfflineMinutes = computed((): number =>
	Math.trunc((userStore.maxOfflineTime || 0) / 60 / 1_000),
);

const cappedOfflineMinutes = computed((): number =>
	Math.min(rawOfflineMinutes.value, rawMaxOfflineMinutes.value),
);

const offlineTimeText = computed((): string => {
	const currentFormatted = formatNumber(cappedOfflineMinutes.value);
	const maxFormatted = formatNumber(rawMaxOfflineMinutes.value);

	const currentText = `${translate("ui.offline_time")}: ${translate("ui.x_minutes").replace("{number}", currentFormatted)}`;
	const maxText = `${translate("ui.max_offline_time")}: ${translate("ui.x_minutes").replace("{number}", maxFormatted)}`;

	return `${currentText} / ${maxText}`;
});

const showMoreButton = computed((): boolean => {
	return userStore.lastTickBefore > moreButtonTime;
});

const recommendedUpgrade = computed((): [UpgradeId, UpgradeItem] | null => {
	const filteredUpgrade =
		Array.from(upgrades.entries()).find(
			(upgrade: [UpgradeId, UpgradeItem]): boolean =>
				upgrade[0] === recommendedUpgradeId &&
				upgrade[1].levelUnlock <= settlementsStore.mainLevel &&
				upgradesStore[upgrade[0]] < upgrade[1].maxLevel,
		) ?? null;
	return filteredUpgrade;
});

const displayRewardToast = (
	rewardsToShow: Partial<Record<ResourceType, number>>,
): void => {
	if (!rewardsToShow) return;
	emitter.emit(EmitterEvents.OfflineIncome, { reward: rewardsToShow });
};

const claimRewards = async (): Promise<void> => {
	if (isPending.value) return;
	isPending.value = true;

	try {
		const currentRewards = { ...rewards.value };

		setLastRecordedActivity(RecordedActivity.OFFLINE_INCOME_CLOSED);

		(
			Object.entries(currentRewards) as Array<[ResourceType, number]>
		).forEach(([resource, value]: [ResourceType, number]): void => {
			resourcesStore.increase(resource, value, "offline income");
		});

		const offlineTicks = tilerepo.getOfflineTickCount();
		if (offlineTicks > 0) {
			tilerepo.handleSettlementCastleTiles(offlineTicks);
		}

		// useSiegeSystemRepository().performOfflineSiege(
		// 	tilerepo.getOfflineTickCount(),
		// );
		// useSiegeSystemStore().increaseOfflineBoosters(
		// 	offlineSiegeBoosters.value,
		// );

		if (offlineAttackClicks.value) {
			const ssr = useSiegeSystemRepository();
			if (ssr.isActive) {
				ssr.increaseAttackClicks(
					offlineAttackClicks.value,
					// true
				);
			}
		}

		await socketService.emitSaveRequest({
			stores: [
				EmitStores.RESOURCES,
				EmitStores.SETTLEMENTS,
				EmitStores.SIEGE_SYSTEM,
			],
		});

		setTimeout((): void => {
			displayRewardToast(currentRewards);
		}, 500);

		emit("closePopup");
	} catch (error) {
		console.error("Failed to claim offline rewards", error);
	} finally {
		isPending.value = false;
	}
};

const queueNextPopups = (): void => {
	// Handle deep link post-offline callback
	const deeplinkCallback = consumePostOfflineCallback();
	if (deeplinkCallback) {
		deeplinkCallback();
	}

	// 1. If eligible for Daily Reward, add it to the queue
	if (
		floorNumber(daysAfterRegistration.value) >
		lastDailyRewardPickedDaysAfterReg.value
	) {
		popupStore.showPopup({
			id: PopupDictionary.DAILY_BONUS,
			type: PopupType.DAILY_REWARD,
			heading: translate("ui.daily_bonus"),
			uniqueById: true,
			skin: SkinTypes.LIGHT,
			maxHeight: 24,
			width: 22.5,
			isPaddingless: true,
		});
	}

	// 2. If there is a valid active popup news, show it
	if (activePopupNews.value) {
		const popupNews = activePopupNews.value;

		// Check if the player has already seen this specific news item
		if (lastSeenNewsTimestamp.value !== popupNews.created_at) {
			popupStore.showPopup({
				id: PopupDictionary.NEWS,
				type: PopupType.NEWS,
				heading: translate("ui.news"),
				body: {
					title: popupNews.title,
					text: popupNews.message,
					date: new Date(popupNews.start_date),
				},
				skin: SkinTypes.DARK,
				maxHeight: 42,
				width: 22,
				isPaddingless: true,
			});

			userStore.setLastSeenNewsTimestamp(popupNews.created_at);
			socketService.emitSaveRequest({ stores: [EmitStores.USER] });
		}
	}

	// 3. Event intro after offline / daily bonus / news.
	void tryShowIntroPopup();
	showIntroPopup();

	questsStore.analyzeQuestState();
};

// Trigger callback when player returns from OfflineModePopup
const handleAdRewardEarned = (): void => {
	// 1. Queue is accessed and the sleeping popup is notified that the ad has run
	popupStore.updateQueuedPopup(
		{ decoratedHeading: translate("ui.offline_earnings") },
		{ componentProps: { isAdWatched: true } },
	);
};

const moreRewards = (): void => {
	if (isPending.value) return;

	setLastRecordedActivity(RecordedActivity.OFFLINE_INCOME_CLOSED);
	popupStore.showPopup({
		id: PopupDictionary.OFFLINE_MORE_AD,
		skin: SkinTypes.LIGHT,
		isPaddingless: true,
		heading: translate("ui.rewarded_ad"),
		width: 20,
		maxHeight: 40,
		isPriority: true,
		component: markRaw(OfflineMorePopup),
		// Pass callback to Props to handle ad completion events
		componentProps: {
			onClaim: handleAdRewardEarned,
			onClose: (): void => {
				emit("closePopup");
			},
		},
	});
};

const toUpgrades = (): void => {
	if (isPending.value) return;

	setLastRecordedActivity(RecordedActivity.OFFLINE_INCOME_CLOSED);
	claimRewards();
};

const calculateOfflineRewards = (): void => {
	const multiplier = props.isAdWatched
		? OFFLINE_INCOME_WITH_AD_MULTIPLIER
		: OFFLINE_INCOME_DEFAULT_MULTIPLIER;

	rewards.value = tilerepo.handleGlobalOfflineProduction(multiplier);
};

onMounted((): void => {
	ticker.stop(GameTickerLockReason.OFFLINE_INCOME);
	segmentManager.run(true);

	calculateOfflineRewards();
	hasAdReward.value = props.isAdWatched ?? false;

	queueNextPopups();

	emitter.on(
		EmitterEvents.GameRecalculateOfflineRewards,
		calculateOfflineRewards,
	);
});

onUnmounted((): void => {
	// Event-start sync may stop the ticker first, only resume if we hold this lock.
	if (ticker.lockReason === GameTickerLockReason.OFFLINE_INCOME) {
		ticker.start(GameTickerLockReason.OFFLINE_INCOME);
	}
	emitter.off(
		EmitterEvents.GameRecalculateOfflineRewards,
		calculateOfflineRewards,
	);
});
</script>

<style lang="scss" scoped>
.offline-popup {
	&__rewards {
		position: relative;
		padding: 1rem 0.75rem;
		min-height: 7.125rem;
		@include flex-col-center;
		margin: 1rem;

		&-info {
			position: relative;
			top: 0.5rem;
			color: #372615;
			font-size: 0.688rem;
			text-align: center;
		}

		&-info + &-info {
			margin-top: 0.25rem;
		}
	}

	&__buttons {
		margin: 0 1rem 0.875rem;
		@include flex-row-center;
		gap: 0.5rem;
	}

	&__upgrade {
		@include flex-col-center;
		position: relative;
		bottom: 0;
		gap: 0.25rem;
		background-color: #27282b;
		padding: 0.75rem;
		padding-top: 0;
		width: 100%;

		&-heading {
			margin-top: 0.25rem;
			color: #f2dcb5;
			font-weight: bold;
			font-size: 1rem;
		}
	}

	:deep(.upgrade-box__heading) {
		align-items: initial;

		& > span {
			top: 0.2rem;
		}
	}

	:deep(.upgrade-box__content) {
		position: relative;
		bottom: 0.25rem;
		padding-right: 0;
	}

	:deep(.ragged-box-bg) {
		position: absolute;
		z-index: -1;
		filter: drop-shadow(0 0 0.1rem rgba(49, 37, 22, 0.6));
		width: 100%;
		height: 100%;
	}

	:deep(.divider-line) {
		position: absolute;
		bottom: 7rem;
		width: 100%;
	}

	:deep(.box-header-bg) {
		position: absolute;
		top: 0;
		width: 100%;
		height: 1.625rem;

		.solid-bg-polygon {
			fill: rgba(145, 113, 56, 0.8) !important;
		}

		.texture-bg-polygon {
			opacity: 0.2 !important;
		}
	}

	.offline-rewards {
		display: flex;
		flex-wrap: wrap;
		justify-content: center;
		gap: 0.5rem;
		width: 98%;
	}

	.offline-reward {
		@include flex-row-center;
		gap: 0.15rem;

		&__value {
			color: #372615;
			font-weight: bold;
			font-size: 1rem;
		}
	}
}
</style>
